import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  addProcessingSoldNoDataToBusiness,
  getProcessingSoldNoDataList,
  updateProcessingSoldNoData,
  deleteProcessingSoldNoData,
} from "../../../../../../apis/Business/ProcessingSoldIntermediateNoDataApi";
import {
  getProcessingIntermediateDropdown,
  getTransportCountryData,
} from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const ProcessingNoDataTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownProcess, setDropdownProcess] = useState([]);
  const [dropdownCountry, setDropdownCountry] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getProcessingSoldNoDataList({
      businessId,
      setObjects: setData,
    });
    getProcessingIntermediateDropdown({ setDropdown: setDropdownProcess });
    getTransportCountryData({ setDropdownData: setDropdownCountry });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0 && dropdownProcess?.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["manufacturing_process.value"]?.value ||
          row.manufacturing_process?.value;

        if (materialValue) {
          const selectedMaterial = dropdownProcess.find(
            (option) =>
              option?.dropdown === materialValue ||
              option?.dropdown_en === materialValue
          );

          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownProcess]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "manufacturing_process.value": {
        targetField: "qty_products_processed",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedItem = dropdownData.find(
            (item) =>
              item.dropdown === selectedValue ||
              item.dropdown_en === selectedValue
          );
          if (selectedItem) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedItem.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedItem.unit,
              }));
            }
          }
        },
        dropdownDataKey: "manufacturing_process.value",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      updateProcessingSoldNoData({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        manufacturing_process: editedItem["manufacturing_process.value"]
          ? editedItem["manufacturing_process.value"].value
          : editedItem["manufacturing_process"]
          ? editedItem["manufacturing_process"].value
          : null,
        sold_intermediate_product: editedItem.sold_intermediate_product,
        country_processed: editedItem["country_processed.value"]
          ? editedItem["country_processed.value"].value
          : editedItem["country_processed"]
          ? editedItem["country_processed"].value
          : null,
        qty_products_processed: editedItem.qty_products_processed,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addProcessingSoldNoDataToBusiness({
        businessId,
        ...newItem,
        manufacturing_process: newItem["manufacturing_process.value"]
          ? newItem["manufacturing_process.value"].value
          : null,
        sold_intermediate_product: newItem.sold_intermediate_product,
        country_processed: newItem["country_processed.value"]
          ? newItem["country_processed.value"].value
          : null,
        qty_products_processed: newItem.qty_products_processed,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProcessingSoldNoData({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "sold_intermediate_product",
        header: t("Sold intermediate product"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "100px",
      },
      {
        accessor: "country_processed.value",
        header: `${t("In which country are your products processed?")}`,
        type: "dropdown",
        dropdownData: dropdownCountry,
        disableSortBy: true,
        width: "130px",
        tipHeader: "Tip",
        tipBody: t("COUNTRY_PROCESSING_TIP"),
      },
      {
        accessor: "manufacturing_process.value",
        header: `${t("Which manufacturing processes emit CO2?")}`,
        type: "dropdown",
        dropdownData: dropdownProcess,
        disableSortBy: true,
        width: "130px",
        tipHeader: "Tip",
        tipBody: t("MANUFACTURING_PROCESS_NO_DATA_TIP"),
      },
      {
        accessor: "qty_products_processed",
        header: `${t("Quantity of products processed in this country")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        unit: (rowId) => `${unitMaterials[rowId] || "Unit"}`,
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [dropdownProcess, dropdownCountry, unitMaterials, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "manufacturing_process.value": dropdownProcess,
      "country_processed.value": dropdownCountry,
    }),
    [dropdownProcess, dropdownCountry]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProcessingNoDataTable;
